/**
 * TemplateLibraryBrowser Component
 *
 * Full-page browser for the template library using the V2 API.
 * Includes search, category filters, and template cards.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  InputBase,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { EDITOR_MAX_WIDTH_WITH_PADDING } from '../constants/layout';
import type { TemplateWithMeta } from '../types';

interface TemplateLibraryBrowserProps {
  /** Available templates from library */
  templates: TemplateWithMeta[];
  /** Template IDs already in user's list */
  excludedTemplateIds: string[];
  /** Whether library is loading */
  isLoading: boolean;
  /** Callback to add template (opt-in for PUBLIC templates) */
  onAddTemplate: (templateId: string) => void;
  /** Callback to close browser */
  onClose: () => void;
}

export const TemplateLibraryBrowser: React.FC<TemplateLibraryBrowserProps> = ({
  templates,
  excludedTemplateIds,
  isLoading,
  onAddTemplate,
  onClose,
}) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');

  // Filter templates based on search
  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const titleMatch = template.displayName.toLowerCase().includes(searchLower);
        const descMatch = template.revision.description?.toLowerCase().includes(searchLower);

        if (!titleMatch && !descMatch) {
          return false;
        }
      }

      return true;
    });
  }, [templates, searchTerm]);

  // Check if template is already in user's list
  const isInUserList = useCallback(
    (templateId: string) => excludedTemplateIds.includes(templateId),
    [excludedTemplateIds],
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchTerm('');
  }, []);

  const hasActiveFilters = searchTerm.length > 0;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: EDITOR_MAX_WIDTH_WITH_PADDING,
        mx: 'auto',
        p: { xs: 2, sm: 3, md: 4 },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            color="text.primary"
            sx={{fontWeight: 600,  mb: 0.5 }}
          >
            Template Library
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Browse and add pre-built templates to your collection
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: 'text.secondary' }}
          aria-label="Close library"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Search */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          px: 2,
          py: 1.5,
          bgcolor: 'background.paper',
          mb: 2,
          transition: theme.transitions.create(['border-color', 'box-shadow'], {
            duration: theme.transitions.duration.short,
          }),
          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
          },
        }}
      >
        <SearchIcon sx={{ color: 'text.secondary', mr: 1.5 }} />
        <InputBase
          fullWidth
          placeholder="Search by name or description..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          inputProps={{
            'aria-label': 'Search templates',
          }}
          sx={{
            '& input::placeholder': {
              color: 'text.secondary',
              opacity: 0.8,
            },
          }}
        />
        {searchTerm && (
          <IconButton
            size="small"
            onClick={() => setSearchTerm('')}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Results Count */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''} found
          {hasActiveFilters && (
            <Typography
              component="span"
              variant="body2"
              color="primary"
              sx={{
                ml: 1,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={clearFilters}
            >
              Clear filters
            </Typography>
          )}
        </Typography>
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Templates List */}
      {!isLoading && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {filteredTemplates.map(template => {
            const inUserList = isInUserList(template.template.id);

            return (
              <Card
                key={template.template.id}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  transition: theme.transitions.create(['border-color', 'box-shadow'], {
                    duration: theme.transitions.duration.short,
                  }),
                  '&:hover': {
                    borderColor: 'primary.light',
                    boxShadow: theme.shadows[2],
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 2,
                    }}
                  >
                    {/* Template Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        color="text.primary"
                        sx={{fontWeight: 600,  mb: 0.5 }}
                      >
                        {template.displayName}
                      </Typography>
                      {template.revision.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1.5 }}
                        >
                          {template.revision.description}
                        </Typography>
                      )}
                    </Box>

                    {/* Actions */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        flexShrink: 0,
                      }}
                    >
                      {inUserList ? (
                        <Chip
                          label="In Your List"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        /* Add Button - adds to list without copying */
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => onAddTemplate(template.template.id)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                          }}
                        >
                          Add
                        </Button>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}

          {/* Empty State */}
          {filteredTemplates.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                No templates found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {hasActiveFilters
                  ? 'Try adjusting your search or filters'
                  : 'No templates are available at this time'}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
